import type { UserCategory } from "@repo/fika-shared/src/lib/user-categories.js"
import { USER_CATEGORIES } from "@repo/fika-shared/src/lib/user-categories.js"
import { useNavigate, useSearchParams } from "@solidjs/router"
import { createEffect, createMemo, createSignal, For, Show } from "solid-js"
import z from "zod"
import { useApplication } from "@/context/session-provider"
import { Button } from "@/designSystem/components/button"
import { FikaLogo } from "@/designSystem/components/fika-logo"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { captureEvent } from "@/lib/observability"
import { Blobs } from "./blobs"
import {
  categoryPillClass,
  categoryPillsContainerClass,
  skipButtonClass,
  subscribedToClass,
} from "./reader-onboarding-page.css"
import { ResponsiveImage } from "./responsive-image"
import {
  buttonWrapperClass,
  descriptionClass,
  formClass,
  imgWrapperClass,
  layoutClass,
  logoLinkClass,
  smallGapClass,
  titleClass,
} from "./writer-onboarding.css"

const searchParamsSchema = z.object({
  publicationTitle: z.string().optional(),
  returnTo: z.string().optional(),
})

export function ReaderOnboardingPage() {
  const { user } = useApplication()
  const [searchParams] = useSearchParams()
  const { LL } = useI18nContext()
  const navigate = useNavigate()

  const params = createMemo(() => searchParamsSchema.safeParse(searchParams))
  const publicationTitle = createMemo(
    () => params().data?.publicationTitle ?? "",
  )
  const returnTo = createMemo(() => params().data?.returnTo)

  createEffect(() => {
    const to = returnTo()
    if (user.readerOnboarded && to) {
      window.location.href = to
    }
  })

  const [selectedCategories, setSelectedCategories] = createSignal<
    Set<UserCategory>
  >(new Set())

  const CompleteReaderOnboardingMutation = graphql(
    `mutation CompleteReaderOnboarding($input: UpdateUserInput!) {
      updateCurrentUser(input: $input) {
        ... on MutationUpdateCurrentUserSuccess {
          data {
            id
            readerOnboarded
            interestCategories
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )

  const [completeOnboardingState, completeOnboarding] =
    createMutationWithErrorHandling(
      CompleteReaderOnboardingMutation,
      "updateCurrentUser",
    )

  function redirectToReturnTo() {
    const to = returnTo()
    if (to) {
      window.location.href = to
    } else {
      navigate("/")
    }
  }

  function toggleCategory(category: UserCategory) {
    const current = selectedCategories()
    const next = new Set(current)
    if (next.has(category)) {
      next.delete(category)
    } else {
      next.add(category)
    }
    setSelectedCategories(next)
  }

  async function handleContinue() {
    const categories = Array.from(selectedCategories()).join(",")

    const result = await completeOnboarding({
      input: {
        readerOnboarded: true,
        interestCategories: categories || null,
      },
    })

    if (result.success) {
      captureEvent("reader_onboarding_completed", {
        categories_count: selectedCategories().size,
      })
    }
    redirectToReturnTo()
  }

  async function handleSkip() {
    const result = await completeOnboarding({
      input: {
        readerOnboarded: true,
      },
    })

    if (result.success) {
      captureEvent("reader_onboarding_skipped", {})
    }
    redirectToReturnTo()
  }

  function getCategoryLabel(category: UserCategory): string {
    switch (category) {
      case "culture":
        return LL().categories.culture()
      case "technology":
        return LL().categories.technology()
      case "business":
        return LL().categories.business()
      case "usPolitics":
        return LL().categories.usPolitics()
      case "finance":
        return LL().categories.finance()
      case "food":
        return LL().categories.food()
      case "podcast":
        return LL().categories.podcast()
      case "sports":
        return LL().categories.sports()
      case "art":
        return LL().categories.art()
      case "worldPolitics":
        return LL().categories.worldPolitics()
      case "healthPolitics":
        return LL().categories.healthPolitics()
      case "news":
        return LL().categories.news()
      case "fashionBeauty":
        return LL().categories.fashionBeauty()
      case "music":
        return LL().categories.music()
      case "faith":
        return LL().categories.faith()
      case "climate":
        return LL().categories.climate()
      case "science":
        return LL().categories.science()
      case "literature":
        return LL().categories.literature()
      case "fiction":
        return LL().categories.fiction()
      case "health":
        return LL().categories.health()
      case "design":
        return LL().categories.design()
      case "travel":
        return LL().categories.travel()
      case "parenting":
        return LL().categories.parenting()
      case "philosophy":
        return LL().categories.philosophy()
      case "comics":
        return LL().categories.comics()
      case "international":
        return LL().categories.international()
      case "crypto":
        return LL().categories.crypto()
      case "history":
        return LL().categories.history()
      case "humor":
        return LL().categories.humor()
      case "education":
        return LL().categories.education()
      case "filmTv":
        return LL().categories.filmTv()
      default:
        return category
    }
  }

  return (
    <div class={layoutClass({ layout: "right" })}>
      <div class={logoLinkClass}>
        <FikaLogo withText />
      </div>
      <div class={imgWrapperClass}>
        <ResponsiveImage src="onboarding-sky" alt="Select interests" />
        <Blobs />
      </div>
      <form
        onSubmit={e => {
          e.preventDefault()
          handleContinue()
        }}
        class={formClass}
      >
        <Show when={publicationTitle()}>
          {publicationTitle => (
            <p class={subscribedToClass}>
              {LL().readerOnboarding.subscribedTo({
                publicationTitle: publicationTitle(),
              })}
            </p>
          )}
        </Show>
        <div class={smallGapClass}>
          <h2 class={titleClass}>{LL().readerOnboarding.selectInterests()}</h2>
          <p class={descriptionClass}>
            {LL().readerOnboarding.selectInterestsDescription()}
          </p>
        </div>

        <div class={categoryPillsContainerClass}>
          <For each={[...USER_CATEGORIES]}>
            {category => (
              <button
                type="button"
                class={categoryPillClass({
                  selected: selectedCategories().has(category),
                })}
                onClick={() => toggleCategory(category)}
              >
                {getCategoryLabel(category)}
              </button>
            )}
          </For>
        </div>

        <div class={buttonWrapperClass}>
          <button
            type="button"
            class={skipButtonClass}
            onClick={handleSkip}
            disabled={completeOnboardingState.fetching}
          >
            {LL().readerOnboarding.skip()}
          </button>
          <Button
            hue="emerald"
            iconName="arrow-right"
            type="submit"
            active={completeOnboardingState.fetching}
          >
            {LL().readerOnboarding.continue()}
          </Button>
        </div>
      </form>
    </div>
  )
}
